import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context';
import { WysiwygEditor, RichDisplay } from '../components/Shared';

const EMPTY = { name: '', slug: '', description: '', founded: '', website: '', logo: '', header_image: '', publishes: [], country: '', status: 'active' };

async function uploadFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return r.data.url;
}

function slugify(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''); }

// ── Publisher detail PAGE (/publishers/:id) ─────────────────────────────────
export function PublisherDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/publishers/${id}`)
      .then(r => {
        const d = r.data;
        if (!Array.isArray(d.publishes)) {
          try { d.publishes = JSON.parse(d.publishes || '[]'); } catch { d.publishes = []; }
        }
        setPub(d);
      })
      .catch(() => setPub(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (payload) => {
    await api.put(`/publishers/${id}`, payload);
    load();
  };

  if (loading) return (
    <div style={{ padding: '80px 20px', textAlign: 'center', color: '#f0c040', fontFamily: 'Cinzel,serif' }}>⏳ Kraunama...</div>
  );
  if (!pub) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 20, color: '#f0c040', marginBottom: 16 }}>Leidėjas nerastas</div>
      <button className="btn btn-secondary" onClick={() => navigate('/publishers')}>← Grįžti</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 0 48px' }}>

      {/* Hero header */}
      <div style={{ position: 'relative', height: 260, background: 'linear-gradient(135deg,#1a0f3c,#2d1a5c)', overflow: 'hidden', marginBottom: 0 }}>
        {pub.header_image
          ? <img src={pub.header_image} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(ellipse at 30% 50%,rgba(240,192,64,0.1),transparent 65%),radial-gradient(ellipse at 70% 20%,rgba(90,62,140,0.4),transparent 60%)' }} />
        }
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(5,2,14,0.92) 0%,rgba(5,2,14,0.4) 55%,transparent 100%)' }} />

        {/* Back button */}
        <button
          onClick={() => navigate('/publishers')}
          style={{ position: 'absolute', top: 16, left: 20, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: 'rgba(200,180,255,0.8)', fontSize: 13, padding: '6px 14px', cursor: 'pointer', zIndex: 2 }}
        >← Grįžti</button>

        {isAdmin && (
          <button
            onClick={() => setEditing(pub)}
            style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(240,192,64,0.15)', border: '1px solid rgba(240,192,64,0.4)', borderRadius: 8, color: '#f0c040', fontSize: 13, padding: '6px 14px', cursor: 'pointer', zIndex: 2 }}
          >✏️ Redaguoti</button>
        )}

        {/* Logo + name */}
        <div style={{ position: 'absolute', bottom: 24, left: 28, display: 'flex', alignItems: 'flex-end', gap: 18, zIndex: 2 }}>
          {pub.logo
            ? <img src={pub.logo} alt={pub.name} style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 12, background: 'rgba(0,0,0,0.6)', padding: 6, border: '1px solid rgba(240,192,64,0.3)', flexShrink: 0 }} />
            : <div style={{ width: 72, height: 72, background: 'rgba(26,15,60,0.8)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, border: '1px solid rgba(240,192,64,0.25)', flexShrink: 0 }}>📚</div>
          }
          <div>
            <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 'clamp(20px,4vw,32px)', color: '#f0c040', fontWeight: 700, margin: '0 0 6px', textShadow: '0 2px 12px rgba(0,0,0,0.9)' }}>{pub.name}</h1>
            <div style={{ fontSize: 13, color: 'rgba(200,180,255,0.75)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {pub.founded && <span>📅 {pub.founded}</span>}
              {pub.country && <span>🌍 {pub.country}</span>}
              {pub.website && <a href={pub.website} target="_blank" rel="noreferrer" style={{ color: '#f0c040', textDecoration: 'none' }}>🔗 Svetainė</a>}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '28px 28px 0', background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>

        {/* Publishes tags */}
        {pub.publishes && pub.publishes.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 10, color: 'rgba(240,192,64,0.5)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Cinzel,serif' }}>{t('publishers.publishes_label')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {pub.publishes.map((item, i) => (
                <span key={i} style={{ background: 'rgba(90,62,140,0.3)', border: '1px solid rgba(90,62,140,0.5)', borderRadius: 8, color: '#c8b4ff', padding: '5px 14px', fontSize: 13, fontWeight: 600 }}>{item}</span>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {pub.description && (
          <div>
            <div style={{ fontSize: 10, color: 'rgba(240,192,64,0.5)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Cinzel,serif' }}>{t('publishers.description_label')}</div>
            <RichDisplay html={pub.description} />
          </div>
        )}
      </div>

      {editing && (
        <AdminEditorModal
          initial={editing}
          onSave={handleUpdate}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ── Drag-drop image uploader ────────────────────────────────────────────────
function ImageDropZone({ value, onChange, label, hint, previewHeight = 60, previewWidth = 120 }) {
  const ref = useRef();
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try { onChange(await uploadFile(files[0])); } catch { /* ignore */ }
    setUploading(false);
  };

  return (
    <div>
      <div
        style={{ border: `2px dashed ${over ? 'rgba(240,192,64,0.6)' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8, padding: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'border-color .2s', background: over ? 'rgba(240,192,64,0.04)' : '#1a1428' }}
        onClick={() => ref.current.click()}
        onDrop={e => { e.preventDefault(); setOver(false); handleFiles(e.dataTransfer.files); }}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
      >
        {value
          ? <img src={value} alt="" style={{ height: previewHeight, width: previewWidth, objectFit: 'contain', borderRadius: 6, background: '#0d0a18' }} />
          : <div style={{ height: previewHeight, width: previewWidth, background: 'rgba(255,255,255,0.04)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🖼️</div>
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'rgba(200,180,255,0.5)', marginBottom: 4 }}>
            {uploading ? '⏳ Įkeliama...' : (label || 'Vilkite arba spustelėkite')}
          </div>
          {hint && <div style={{ fontSize: 10, color: 'rgba(200,180,255,0.3)' }}>{hint}</div>}
          {value && <div style={{ fontSize: 10, color: 'rgba(200,180,255,0.4)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>}
        </div>
        {value && (
          <button style={{ background: 'none', border: '1px solid rgba(192,57,43,0.4)', color: '#ff8080', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}
            onClick={e => { e.stopPropagation(); onChange(''); }}>✕</button>
        )}
      </div>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.gif,.webp" style={{ display: 'none' }}
        onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
    </div>
  );
}

// ── Admin editor modal ──────────────────────────────────────────────────────
function AdminEditorModal({ initial, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    ...EMPTY,
    ...initial,
    publishes: Array.isArray(initial?.publishes) ? initial.publishes : [],
  });
  const [saving, setSaving] = useState(false);
  const [pubInput, setPubInput] = useState('');

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addPub = () => {
    const v = pubInput.trim();
    if (v && !form.publishes.includes(v)) setForm(f => ({ ...f, publishes: [...f.publishes, v] }));
    setPubInput('');
  };
  const removePub = (i) => setForm(f => ({ ...f, publishes: f.publishes.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.name), publishes: JSON.stringify(form.publishes) };
      await onSave(payload);
      onClose();
    } finally { setSaving(false); }
  };

  const inp = { width: '100%', background: '#1a1428', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: '#fff', padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, color: 'rgba(200,180,255,0.6)', letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' };

  return (
    <div className="edit-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="edit-modal-box">
        {/* Header */}
        <div className="edit-modal-header" style={{ background: '#0d0a18' }}>
          <span style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: '#f0c040' }}>
            {initial?.id ? `✏️ ${t('publishers.edit_title')}` : `➕ ${t('publishers.new_title')}`}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div className="edit-modal-body" style={{ background: '#0d0a18' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

            {/* Header image */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={lbl}>Header nuotrauka <span style={{ opacity: .5, fontWeight: 400 }}>(rekomenduojama: 1200×200px)</span></label>
              <ImageDropZone
                value={form.header_image}
                onChange={v => set('header_image', v)}
                label="Vilkite arba spustelėkite norėdami įkelti header nuotrauką"
                hint="PNG, JPG, WEBP — rekomenduojama 1200×200px"
                previewHeight={56}
                previewWidth={200}
              />
            </div>

            {/* Logo */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={lbl}>{t('publishers.field_logo')}</label>
              <ImageDropZone
                value={form.logo}
                onChange={v => set('logo', v)}
                label="Vilkite arba spustelėkite norėdami įkelti logotipą"
                hint="PNG, JPG, WEBP — rekomenduojama 200×200px"
                previewHeight={56}
                previewWidth={56}
              />
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={lbl}>{t('publishers.field_name')}</label>
              <input style={inp} value={form.name} onChange={setF('name')} placeholder="Wizards of the Coast" />
            </div>
            <div>
              <label style={lbl}>{t('publishers.field_slug')}</label>
              <input style={inp} value={form.slug} onChange={setF('slug')} placeholder={slugify(form.name || 'leideja')} />
            </div>
            <div>
              <label style={lbl}>{t('publishers.field_founded')}</label>
              <input style={inp} value={form.founded} onChange={setF('founded')} placeholder="1990" />
            </div>
            <div>
              <label style={lbl}>{t('publishers.field_country')}</label>
              <input style={inp} value={form.country} onChange={setF('country')} placeholder="JAV" />
            </div>
            <div>
              <label style={lbl}>{t('publishers.field_website')}</label>
              <input style={inp} value={form.website} onChange={setF('website')} placeholder="https://company.com" />
            </div>
            <div>
              <label style={lbl}>{t('publishers.field_status')}</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.status} onChange={setF('status')}>
                <option value="active">{t('publishers.status_active')}</option>
                <option value="hidden">{t('publishers.status_hidden')}</option>
              </select>
            </div>

            {/* Publishes tags */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={lbl}>{t('publishers.field_publishes')}</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input style={{ ...inp, flex: 1 }} value={pubInput} onChange={e => setPubInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addPub())} placeholder="D&D, Pathfinder... (Enter)" />
                <button onClick={addPub} style={{ background: 'rgba(240,192,64,0.15)', border: '1px solid rgba(240,192,64,0.3)', borderRadius: 6, color: '#f0c040', padding: '8px 16px', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>+ {t('publishers.add_tag')}</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {form.publishes.map((item, i) => (
                  <span key={i} style={{ background: 'rgba(90,62,140,0.3)', border: '1px solid rgba(90,62,140,0.5)', borderRadius: 6, color: '#c8b4ff', padding: '3px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {item}
                    <button onClick={() => removePub(i)} style={{ background: 'none', border: 'none', color: '#f0c040', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* WYSIWYG description */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={lbl}>{t('publishers.field_description')}</label>
              <WysiwygEditor
                value={form.description}
                onChange={v => set('description', v)}
                placeholder="Leidėjo aprašymas..."
                minHeight={200}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="edit-modal-footer" style={{ background: '#0d0a18', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(255,255,255,0.7)', padding: '10px 20px', cursor: 'pointer', fontSize: 13 }}>{t('publishers.cancel')}</button>
          <button onClick={save} disabled={saving || !form.name.trim()} style={{ background: 'linear-gradient(135deg,#5a3e8c,#7b5cbf)', border: 'none', borderRadius: 8, color: '#fff', padding: '10px 24px', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: saving ? 0.6 : 1 }}>
            {saving ? t('publishers.saving') : `💾 ${t('publishers.save')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const PUB_PAGE_SIZE = 24;

// ── Horizontal publisher card (4-per-row in catalog) ─────────────────────────
function PubCardH({ pub, onNavigate, onEdit, onDelete, isAdmin, t }) {
  return (
    <div
      onClick={() => onNavigate(pub.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        background: 'linear-gradient(135deg,#0d0820,#150d2a)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 10, padding: '12px 14px',
        cursor: 'pointer', transition: 'all .18s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(240,192,64,0.35)'; e.currentTarget.style.transform='translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.07)'; e.currentTarget.style.transform='none'; }}
    >
      {pub.logo ? (
        <img src={pub.logo} alt={pub.name} style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 7, background: '#1a0f3c', padding: 3, flexShrink: 0 }} />
      ) : (
        <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#1a0f3c,#2d1a5c)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>📚</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 12, color: '#f0c040', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pub.name}</div>
        <div style={{ fontSize: 10, color: 'rgba(200,180,255,0.45)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {[pub.founded && `📅 ${pub.founded}`, pub.country && `🌍 ${pub.country}`].filter(Boolean).join('  ') || ' '}
        </div>
      </div>
      {isAdmin && (
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => onEdit(pub)} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 5, color: 'rgba(200,180,255,0.6)', padding: '3px 6px', cursor: 'pointer', fontSize: 11 }}>✏️</button>
          <button onClick={() => onDelete(pub.id)} style={{ background: 'rgba(255,60,60,0.08)', border: 'none', borderRadius: 5, color: 'rgba(255,100,100,0.7)', padding: '3px 6px', cursor: 'pointer', fontSize: 11 }}>🗑️</button>
        </div>
      )}
    </div>
  );
}

// ── Pagination for publishers ─────────────────────────────────────────────────
function PubPageNav({ page, total, onChange }) {
  const pages = Math.ceil(total / PUB_PAGE_SIZE);
  if (pages <= 1) return null;
  const btn = (p, label, disabled) => (
    <button key={p + label} onClick={() => !disabled && onChange(p)} disabled={disabled} style={{
      minWidth: 34, height: 34, padding: '0 8px',
      background: p === page ? 'rgba(240,192,64,0.18)' : 'rgba(255,255,255,0.04)',
      border: `1px solid ${p === page ? 'rgba(240,192,64,0.45)' : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 6, color: p === page ? '#f0c040' : 'rgba(200,180,255,0.65)',
      cursor: disabled ? 'default' : 'pointer', fontSize: 13,
      fontWeight: p === page ? 700 : 400, opacity: disabled ? 0.3 : 1,
      fontFamily: p === page ? 'Cinzel,serif' : 'inherit',
    }}>{label}</button>
  );
  const nums = [];
  for (let i = 1; i <= pages; i++) {
    if (i === 1 || i === pages || (i >= page - 2 && i <= page + 2)) nums.push(i);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, margin: '36px 0 8px' }}>
      {btn(page - 1, '←', page === 1)}
      {nums.map((n, i) => n === '…'
        ? <span key={'e' + i} style={{ color: 'rgba(200,180,255,0.3)', padding: '0 4px' }}>…</span>
        : btn(n, n, false)
      )}
      {btn(page + 1, '→', page === pages)}
    </div>
  );
}

// ── Main PublishersPage ─────────────────────────────────────────────────────
export default function PublishersPage() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null);
  // catalog state
  const [view,   setView]   = useState('featured'); // 'featured' | 'catalog'
  const [letter, setLetter] = useState('');         // '' = all
  const [page,   setPage]   = useState(1);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    const endpoint = isAdmin ? '/admin/publishers' : '/publishers';
    setLoading(true);
    api.get(endpoint)
      .then(r => setPublishers(r.data))
      .catch(() => setPublishers([]))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (payload) => { await api.post('/publishers', payload); load(); };
  const handleUpdate = async (payload) => { await api.put(`/publishers/${editing.id}`, payload); load(); };
  const handleDelete = async (id) => {
    if (!window.confirm(t('publishers.delete_confirm'))) return;
    await api.delete(`/publishers/${id}`); load();
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(publishers, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'leidejai.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const rows = publishers.map(p => ({
      ID: p.id, Pavadinimas: p.name, Salis: p.country || '',
      Ikurta: p.founded || '', Svetaine: p.website || '',
      Leidzia: Array.isArray(p.publishes) ? p.publishes.join(', ') : '',
      Statusas: p.status,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('publishers.title'));
    XLSX.writeFile(wb, 'leidejai.xlsx');
  };

  const importJSON = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const items = Array.isArray(JSON.parse(ev.target.result)) ? JSON.parse(ev.target.result) : [JSON.parse(ev.target.result)];
        for (const item of items) {
          const payload = {
            name: item.name || item.Pavadinimas || '', slug: item.slug || slugify(item.name || ''),
            description: item.description || '', founded: item.founded || item.Ikurta || '',
            website: item.website || item.Svetaine || '', logo: item.logo || '',
            publishes: JSON.stringify(Array.isArray(item.publishes) ? item.publishes : (item.Leidzia || '').split(',').map(s => s.trim()).filter(Boolean)),
            country: item.country || item.Salis || '', status: item.status || 'active',
          };
          if (payload.name) await api.post('/publishers', payload);
        }
        load();
      } catch (err) { alert('JSON failo klaida: ' + err.message); }
    };
    reader.readAsText(file); e.target.value = '';
  };

  // 5 random publishers for featured view — stable per load
  const featured5 = useMemo(() => {
    if (!publishers.length) return [];
    return [...publishers].sort(() => Math.random() - 0.5).slice(0, 5);
  }, [publishers.length]); // eslint-disable-line

  // sorted alphabetically, filtered by selected letter
  const catalogAll = useMemo(() => {
    const sorted = [...publishers].sort((a, b) => a.name.localeCompare(b.name));
    return letter ? sorted.filter(p => p.name.charAt(0).toUpperCase() === letter) : sorted;
  }, [publishers, letter]);

  const pageItems = useMemo(
    () => catalogAll.slice((page - 1) * PUB_PAGE_SIZE, page * PUB_PAGE_SIZE),
    [catalogAll, page]
  );

  // letters that actually have publishers
  const activeLetters = useMemo(
    () => new Set(publishers.map(p => p.name.charAt(0).toUpperCase())),
    [publishers]
  );

  const switchLetter = (l) => { setLetter(l); setView('catalog'); setPage(1); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const showAll      = ()  => { setLetter(''); setView('catalog'); setPage(1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 20px' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 28, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 32 }}>📚</span>
            <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 28, color: '#f0c040', margin: 0, fontWeight: 700 }}>{t('publishers.title')}</h1>
          </div>
          <p style={{ color: 'rgba(200,180,255,0.6)', fontSize: 14, margin: 0 }}>
            {t('publishers.subtitle')} — {publishers.length} {t('publishers.count_many')}
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setEditing(EMPTY)} style={{ background: 'linear-gradient(135deg,#5a3e8c,#7b5cbf)', border: 'none', borderRadius: 8, color: '#fff', padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>➕ {t('publishers.add')}</button>
            <button onClick={exportJSON}   style={{ background: 'rgba(240,192,64,0.1)',  border: '1px solid rgba(240,192,64,0.3)',  borderRadius: 8, color: '#f0c040', padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>⬇ JSON</button>
            <button onClick={exportExcel}  style={{ background: 'rgba(80,160,80,0.1)',   border: '1px solid rgba(80,160,80,0.3)',   borderRadius: 8, color: '#80d080', padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>⬇ EXCEL</button>
            <button onClick={() => fileRef.current?.click()} style={{ background: 'rgba(80,120,200,0.1)', border: '1px solid rgba(80,120,200,0.3)', borderRadius: 8, color: '#80a8ff', padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>⬆ IMPORT JSON</button>
            <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={importJSON} />
          </div>
        )}
      </div>

      {/* ── A-Z Index Bar ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 28, padding: '16px 18px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10 }}>
        <button
          onClick={() => showAll()}
          style={{
            background: view === 'catalog' && letter === '' ? 'rgba(240,192,64,0.2)' : 'transparent',
            border: `1px solid ${view === 'catalog' && letter === '' ? 'rgba(240,192,64,0.5)' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 5, color: view === 'catalog' && letter === '' ? '#f0c040' : 'rgba(200,180,255,0.5)',
            padding: '4px 10px', cursor: 'pointer', fontFamily: 'Cinzel,serif', fontSize: 11, fontWeight: 700,
          }}
        >Visi</button>
        {ALPHABET.map(l => (
          <button
            key={l}
            onClick={() => activeLetters.has(l) && switchLetter(l)}
            style={{
              width: 30, height: 30,
              background: letter === l ? 'rgba(240,192,64,0.2)' : 'transparent',
              border: `1px solid ${letter === l ? 'rgba(240,192,64,0.5)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 5,
              color: letter === l ? '#f0c040' : activeLetters.has(l) ? 'rgba(200,180,255,0.7)' : 'rgba(255,255,255,0.15)',
              cursor: activeLetters.has(l) ? 'pointer' : 'default',
              fontFamily: 'Cinzel,serif', fontSize: 11, fontWeight: 700,
              transition: 'all .15s',
            }}
            onMouseEnter={e => { if (activeLetters.has(l) && letter !== l) e.currentTarget.style.borderColor = 'rgba(240,192,64,0.3)'; }}
            onMouseLeave={e => { if (letter !== l) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          >{l}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#f0c040', fontSize: 14 }}>{t('publishers.loading')}</div>
      ) : publishers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📚</div>
          <div style={{ color: '#f0c040', fontSize: 15 }}>{t('publishers.empty')}</div>
          {isAdmin && (
            <button onClick={() => setEditing(EMPTY)} style={{ marginTop: 16, background: 'rgba(240,192,64,0.1)', border: '1px solid rgba(240,192,64,0.3)', borderRadius: 8, color: '#f0c040', padding: '10px 20px', cursor: 'pointer', fontSize: 13 }}>
              {t('publishers.add_first')}
            </button>
          )}
        </div>
      ) : view === 'featured' ? (
        /* ── Featured (landing) view ── */
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ height: 1, background: 'linear-gradient(90deg,rgba(240,192,64,0.4),transparent)', flex: 1 }} />
            <span style={{ fontFamily: 'Cinzel,serif', fontSize: 12, color: '#f0c040', letterSpacing: 3 }}>IŠSKIRTINIAI LEIDĖJAI</span>
            <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(240,192,64,0.4))', flex: 1 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 18, marginBottom: 32 }}>
            {featured5.map(pub => (
              <div
                key={pub.id}
                style={{ background: 'linear-gradient(135deg,#0d0820,#150d2a)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', transition: 'all .2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(240,192,64,0.35)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.07)'; e.currentTarget.style.transform='none'; }}
                onClick={() => navigate(`/publishers/${pub.id}`)}
              >
                {/* Header image strip */}
                <div style={{ position: 'relative', height: 80, background: 'linear-gradient(135deg,#1a0f3c,#2d1a5c)', overflow: 'hidden' }}>
                  {pub.header_image && <img src={pub.header_image} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(13,8,32,0.85) 0%,transparent 100%)' }} />
                  {isAdmin && (
                    <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4, zIndex: 2 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => setEditing(pub)} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 5, color: 'rgba(200,180,255,0.8)', padding: '3px 7px', cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={() => handleDelete(pub.id)} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 5, color: 'rgba(255,100,100,0.8)', padding: '3px 7px', cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                    </div>
                  )}
                </div>
                <div style={{ padding: '14px 18px 14px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.05)', marginTop: -24, position: 'relative', zIndex: 1 }}>
                  {pub.logo
                    ? <img src={pub.logo} alt={pub.name} style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8, background: 'rgba(0,0,0,0.6)', padding: 3, border: '1px solid rgba(240,192,64,0.25)', flexShrink: 0 }} />
                    : <div style={{ width: 44, height: 44, background: 'rgba(26,15,60,0.8)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, border: '1px solid rgba(240,192,64,0.2)', flexShrink: 0 }}>📚</div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Cinzel,serif', fontSize: 13, color: '#f0c040', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pub.name}</div>
                    <div style={{ fontSize: 11, color: 'rgba(200,180,255,0.5)', marginTop: 2 }}>
                      {[pub.founded && `📅 ${pub.founded}`, pub.country && `🌍 ${pub.country}`].filter(Boolean).join('  ')}
                    </div>
                  </div>
                </div>
                <div style={{ padding: '14px 20px' }}>
                  {Array.isArray(pub.publishes) && pub.publishes.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {pub.publishes.slice(0, 5).map((item, i) => (
                        <span key={i} style={{ background: 'rgba(90,62,140,0.25)', border: '1px solid rgba(90,62,140,0.4)', borderRadius: 5, color: '#c8b4ff', padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>{item}</span>
                      ))}
                      {pub.publishes.length > 5 && <span style={{ color: '#f0c040', fontSize: 10 }}>+{pub.publishes.length - 5}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={showAll}
              style={{ background: 'linear-gradient(135deg,rgba(90,62,140,0.3),rgba(123,92,191,0.3))', border: '1px solid rgba(123,92,191,0.45)', borderRadius: 10, color: '#c8b4ff', padding: '12px 36px', cursor: 'pointer', fontFamily: 'Cinzel,serif', fontSize: 13, fontWeight: 700, letterSpacing: 2, transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.background='linear-gradient(135deg,rgba(90,62,140,0.5),rgba(123,92,191,0.5))'; e.currentTarget.style.color='#f0c040'; }}
              onMouseLeave={e => { e.currentTarget.style.background='linear-gradient(135deg,rgba(90,62,140,0.3),rgba(123,92,191,0.3))'; e.currentTarget.style.color='#c8b4ff'; }}
            >📚 Daugiau leidėjų ({publishers.length})</button>
          </div>
        </div>
      ) : (
        /* ── Catalog (A-Z) view ── */
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ height: 1, background: 'linear-gradient(90deg,rgba(240,192,64,0.4),transparent)', flex: 1 }} />
            <span style={{ fontFamily: 'Cinzel,serif', fontSize: 12, color: '#f0c040', letterSpacing: 3 }}>
              {letter ? `LEIDĖJAI — ${letter}` : `VISI LEIDĖJAI (${publishers.length})`}
            </span>
            <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(240,192,64,0.4))', flex: 1 }} />
          </div>

          {catalogAll.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'rgba(200,180,255,0.4)' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📚</div>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, letterSpacing: 2 }}>LEIDĖJŲ NERASTA</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
                {pageItems.map(pub => (
                  <PubCardH
                    key={pub.id}
                    pub={pub}
                    onNavigate={id => navigate(`/publishers/${id}`)}
                    onEdit={setEditing}
                    onDelete={handleDelete}
                    isAdmin={isAdmin}
                    t={t}
                  />
                ))}
              </div>
              <PubPageNav page={page} total={catalogAll.length} onChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              onClick={() => { setView('featured'); setLetter(''); }}
              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: 'rgba(200,180,255,0.5)', padding: '8px 20px', cursor: 'pointer', fontSize: 12, transition: 'all .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='rgba(240,192,64,0.3)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='rgba(255,255,255,0.1)'}
            >← Grįžti į pradžią</button>
          </div>
        </div>
      )}

      {editing !== null && (
        <AdminEditorModal
          initial={editing}
          onSave={editing.id ? handleUpdate : handleCreate}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
